import { Modal } from '../Modal';
import { BossIcon } from './BossIcon';
import {
  difficultyOptionLabel,
  groupBossesByTier,
  ineligibilityReason,
  type CharacterForce,
} from '../../lib/bossGrouping';
import type { BossDto } from '../../lib/reference';
import type { BossMatrixCharacter } from '../../lib/bosses';

interface AddBossesModalProps {
  character: BossMatrixCharacter;
  bossCatalog: BossDto[];
  /** `5` on a Heroic account, `1` otherwise — see `BossCellProps.crystalMultiplier`. */
  crystalMultiplier: number;
  planPendingKey: string | null;
  onSetDifficulty: (boss: BossDto, difficulty: string) => void;
  onClose: () => void;
}

/**
 * The "Add bosses" picker (character-tab redesign): the full catalog, grouped/unlock-sorted the
 * same way the boss list itself is, with requirement-gated difficulties greyed via the existing
 * `ineligibilityReason` logic. Picking a difficulty immediately upserts the plan — the same
 * select-fires-the-mutation pattern `BossCell` already uses on the matrix — so there is no separate
 * "confirm" step and a player can add several bosses in one sitting without the modal closing
 * between picks. A boss already on the list is highlighted and its select shows the current
 * difficulty; picking a different one there updates it in place.
 */
export function AddBossesModal({
  character,
  bossCatalog,
  crystalMultiplier,
  planPendingKey,
  onSetDifficulty,
  onClose,
}: AddBossesModalProps) {
  const plannedDifficulty = new Map(character.plans.map((p) => [p.bossSlug, p.difficulty]));
  const groups = groupBossesByTier(bossCatalog);
  const characterForce: CharacterForce = {
    level: character.level,
    arcaneForce: character.arcaneForce,
    sacredForce: character.sacredForce,
  };

  return (
    <Modal title={`Add bosses for ${character.name}`} onClose={onClose}>
      <p className="mb-4 text-sm text-text-muted">
        Pick a difficulty to add a boss to {character.name}&rsquo;s list. Add as many as you like,
        then close when you&rsquo;re done.
      </p>
      <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
        {groups.map((group) => (
          <div key={group.tier}>
            <h3 className="mb-2 font-display text-xs font-semibold uppercase tracking-widest text-text-faint">
              {group.label}
            </h3>
            <ul className="space-y-2">
              {group.bosses.map((boss) => {
                const onList = plannedDifficulty.has(boss.slug);
                const pending = planPendingKey === `${character.characterId}:${boss.slug}`;
                return (
                  <li
                    key={boss.slug}
                    className={[
                      'flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 transition-colors',
                      onList ? 'border-accent-border bg-accent-dim' : 'border-border bg-bg-elevated-hover',
                    ].join(' ')}
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <BossIcon slug={boss.slug} name={boss.name} />
                      <span className="truncate text-sm font-medium text-text">{boss.name}</span>
                      {onList && (
                        <span className="shrink-0 text-[0.625rem] font-semibold uppercase tracking-wide text-accent-strong">
                          On list
                        </span>
                      )}
                    </span>
                    <select
                      aria-label={`Add ${boss.name} for ${character.name}`}
                      value={plannedDifficulty.get(boss.slug) ?? ''}
                      disabled={pending}
                      onChange={(e) => onSetDifficulty(boss, e.target.value)}
                      className="min-h-tap max-w-[10.5rem] shrink-0 rounded-md border border-border-strong bg-bg-elevated px-1.5 text-xs text-text transition-colors focus:border-accent focus:outline-none disabled:opacity-50"
                    >
                      <option value="" disabled>
                        {onList ? 'Change difficulty' : 'Add'}
                      </option>
                      {boss.difficulties.map((d) => {
                        const reason = ineligibilityReason(characterForce, d);
                        return (
                          <option
                            key={d.difficulty}
                            value={d.difficulty}
                            disabled={reason !== null}
                            title={reason ?? undefined}
                          >
                            {difficultyOptionLabel(
                              d.difficulty,
                              d.crystalValue === null ? null : d.crystalValue * crystalMultiplier,
                            )}
                            {reason ? ' ⚠' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="min-h-tap rounded-lg bg-accent px-5 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}
